<template>
    <div class="input-range">
        <InputSlider
            v-bind="$attrs"
            ref="slider"
            :model-value="modelValue"
            :min="min"
            :max="max"
            :interval="interval"
            :data="data"
            :data-value="dataValue"
            :data-label="dataLabel"
            @update:model-value="$emit('update:model-value', $event)"
        />
        <div class="flex justify-between mt-1">
            <template v-if="isNumericRange">
                <InputField
                    ref="min"
                    type="number"
                    :model-value="modelValue[0]"
                    :max="max"
                    :min="min"
                    size="1"
                    :step="interval"
                    :disabled="$attrs.disabled"
                    class="w-[60px] h-10 font-body-3-regular text-center text-content-secondary"
                    @update:model-value="setMinDebounced"
                    @blur="setMin($event.target.value)"
                />
                <InputField
                    ref="max"
                    type="number"
                    :model-value="modelValue[1]"
                    :max="max"
                    :min="min"
                    :step="interval"
                    :disabled="$attrs.disabled"
                    class="w-[60px] h-10 font-body-3-regular text-center text-content-secondary"
                    @update:model-value="setMaxDebounced"
                    @blur="setMax($event.target.value)"
                />
            </template>

            <template v-else>
                <div
                    class="min-w-[60px] h-10 px-3 font-body-3-regular text-center text-content-secondary bg-white border border-slate-200 rounded-lg flex items-center justify-center"
                    v-text="minData"
                ></div>
                <div
                    class="min-w-[60px] h-10 px-3 font-body-3-regular text-center text-content-secondary bg-white border border-slate-200 rounded-lg flex items-center justify-center"
                    v-text="maxData"
                ></div>
            </template>
        </div>
    </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { defineComponent } from 'vue';
import InputSlider from '@/components/InputSlider';
import { debounce } from 'lodash-es';

type DataObjectArray = Array<Record<string, unknown>>;

/**
 * Range slider based on https://nightcatsama.github.io/vue-slider-component
 */
export default defineComponent({
    name: 'InputRange',
    components: {
        InputSlider,
    },
    inheritAttrs: false,
    props: {
        /**
         * Selected range
         */
        modelValue: {
            type: Array as PropType<Array<string | number>>,
            required: true,
        },
        /**
         * Minimum value of a numeric range
         */
        min: {
            type: Number,
            default: undefined,
        },
        /**
         * Maximum value of a numeric range
         */
        max: {
            type: Number,
            default: undefined,
        },
        /**
         * The interval of a numeric range
         */
        interval: {
            type: Number,
            default: 1,
        },
        /**
         * Custom non-numeric data range, e.g. if you want to have a range over complex data instead of just numbers
         * This does not allow editing the min/max values anymore.
         * Example: `[{value: 1, label: 'Semester 1'},{value: 2, label: 'Semester 2'}]` or `['Semester 1', 'Semester 2']`
         */
        data: {
            type: Array as PropType<string[] | DataObjectArray>,
            default: undefined,
        },
        /**
         * Only valid when `data` is `Array<object>`.
         * This prop specifies from which field of the `data` objects the input value should be taken.
         */
        dataValue: {
            type: String as PropType<keyof DataObjectArray[0]>,
            default: 'value',
        },
        /**
         * Only valid when `data` is `Array<object>`.
         * This prop specifies from which field of the `data` objects the label should be taken.
         */
        dataLabel: {
            type: String,
            default: 'label',
        },
    },
    emits: ['update:model-value'],
    computed: {
        isNumericRange() {
            return !this.data?.length;
        },
        isObjectRange(): boolean {
            return !!this.data?.length && this.data[0] instanceof Object;
        },
        minData() {
            return this.getDataLabel(this.modelValue[0]);
        },
        maxData() {
            return this.getDataLabel(this.modelValue[1]);
        },
    },
    methods: {
        setMinDebounced: debounce(function setMinDebounced(n: number) {
            // @ts-ignore cannot resolve this
            this.setMin(n);
        }, 1000),
        setMaxDebounced: debounce(function setMaxDebounced(n: number) {
            // @ts-ignore cannot resolve this
            this.setMax(n);
        }, 1000),
        setMin(n: number) {
            if (!this.isNumericRange || this.min === undefined || this.max === undefined) return;

            // making sure the value is still in the interval boundaries
            const val = Math.max(Math.min(this.max, n), this.min);
            this.setValue([val, Math.max(val, this.modelValue[1] as number)]);
        },
        setMax(n: number) {
            if (!this.isNumericRange || this.max === undefined || this.min === undefined) return;

            // making sure the value is still in the interval boundaries
            const val = Math.min(Math.max(this.min, n), this.max);
            this.setValue([Math.min(this.modelValue[0] as number, val), val]);
        },
        setValue(val: number[]) {
            // Values from the user outside of the min/max range, would not be resetted through the reactivity api,
            // this we are doing it directly here.

            // @ts-ignore cannot resolve ref
            this.$refs.slider.setValue(val);
            // @ts-ignore cannot resolve ref
            this.$refs.min.setValue(val[0]);
            // @ts-ignore cannot resolve ref
            this.$refs.max.setValue(val[1]);

            this.$emit('update:model-value', val);
        },
        getDataLabel(value: unknown): unknown {
            return this.isObjectRange
                ? (this.data as DataObjectArray).find((val) => val[this.dataValue] === value)?.[this.dataLabel]
                : value;
        },
    },
});
</script>

<style lang="scss" scoped>
.input-range :deep(.input-field) {
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        display: none;
        -webkit-appearance: none;
        margin: 0; // <-- Apparently some margin are still there even though it's hidden
    }

    input[type='number'] {
        appearance: textfield;
        -moz-appearance: textfield; /* Firefox */
    }
}
</style>
